import { useState } from 'react';
import { submitLead } from '../services/api';
import { trackFormSubmit } from '../services/analytics';

interface FormData {
  businessName: string;
  revenueTier: string;
  industry: string;
  teamSize: string;
  painPoint: string;
  currentTools: string;
  hoursLost: string;
  brokenSystems: string[];
  vision: string;
  investmentReadiness: string;
  howFound: string;
  fullName: string;
  email: string;
  phone: string;
  consultFormat: string;
  consent: boolean;
}

const initialFormData: FormData = {
  businessName: '', revenueTier: '', industry: '', teamSize: '',
  painPoint: '', currentTools: '', hoursLost: '', brokenSystems: [],
  vision: '', investmentReadiness: '', howFound: '',
  fullName: '', email: '', phone: '', consultFormat: '', consent: false,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '2px',
  color: '#f4f1ea',
  padding: '0.9rem 1.2rem',
  fontFamily: 'var(--font-sans)',
  fontSize: '0.95rem',
  outline: 'none',
  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-sans)',
  fontSize: '0.8rem',
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: '#a9a7af',
  marginBottom: '0.5rem',
  fontWeight: 500,
};

const fieldStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
};

const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const FlowStateDiagnostic = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const update = (field: keyof FormData, value: string | boolean | string[]) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  const toggleBrokenSystem = (val: string) => {
    setFormData(prev => ({
      ...prev,
      brokenSystems: prev.brokenSystems.includes(val)
        ? prev.brokenSystems.filter(s => s !== val)
        : [...prev.brokenSystems, val],
    }));
  };

  const focusedInput = (name: string): React.CSSProperties => ({
    ...inputStyle,
    borderColor: focusedField === name ? '#a2cca9' : 'rgba(255,255,255,0.08)',
    boxShadow: focusedField === name ? '0 0 12px rgba(162, 204, 169, 0.15)' : 'none',
  });

  const canAdvance = () => {
    if (step === 1) return formData.businessName.trim().length > 0;
    if (step === 2) return formData.painPoint.trim().length > 0;
    if (step === 3) return formData.vision.trim().length > 0;
    if (step === 4) return formData.fullName.trim().length > 0 && formData.email.includes('@') && formData.consent;
    return false;
  };

  const stepLabels = ['Context', 'Pain', 'Vision', 'Protocol'];

  if (submitted) {
    return (
      <div style={{
        padding: '4rem 3.5rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Ambient glow */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '300px', height: '300px',
          background: 'radial-gradient(circle, rgba(162, 204, 169,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        {/* Checkmark */}
        <div style={{ marginBottom: '2rem' }}>
          <svg width="72" height="72" viewBox="0 0 72 72" fill="none" style={{ display: 'block', margin: '0 auto' }}>
            <circle cx="36" cy="36" r="35" stroke="#a2cca9" strokeWidth="1.5" opacity="0.4" />
            <circle cx="36" cy="36" r="28" stroke="#a2cca9" strokeWidth="1" opacity="0.2" />
            <path d="M22 36l10 10 18-20" stroke="#a2cca9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '2rem', color: '#f4f1ea', marginBottom: '1rem', fontWeight: 400 }}>
          Your FlowState Protocol<br /><em>is Initiated</em>
        </h3>
        <p style={{ color: '#a9a7af', fontFamily: 'var(--font-sans)', lineHeight: 1.7, maxWidth: '420px', margin: '0 auto', fontSize: '0.95rem' }}>
          Latoya will personally review your diagnostic within 48 hours. Prepare to receive a tailored systems blueprint.
        </p>
        <div style={{ marginTop: '2.5rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
          {[0,1,2].map(i => (
            <div key={i} style={{
              width: '4px', height: '4px', borderRadius: '50%',
              background: '#a2cca9',
              opacity: 0.4 + i * 0.3,
              animation: `dotPulse 1.4s ease-in-out ${i * 0.2}s infinite`,
            }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Progress Indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginBottom: '2.5rem' }}>
        {stepLabels.map((label, i) => {
          const n = i + 1;
          const isActive = n === step;
          const isDone = n < step;
          return (
            <div key={n} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem' }}>
                <div style={{
                  width: '10px', height: '10px', borderRadius: '50%',
                  background: isDone || isActive ? '#a2cca9' : 'rgba(255,255,255,0.12)',
                  boxShadow: isActive ? '0 0 14px rgba(162, 204, 169,0.7)' : 'none',
                  transition: 'all 0.4s ease',
                }} />
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: isActive ? '#a2cca9' : isDone ? '#a9a7af' : 'rgba(255,255,255,0.25)',
                }}>{label}</span>
              </div>
              {i < stepLabels.length - 1 && (
                <div style={{
                  flex: 1, height: '1px', margin: '-1rem 0.5rem 0 0.5rem',
                  background: isDone ? 'rgba(162, 204, 169,0.4)' : 'rgba(255,255,255,0.06)',
                  transition: 'background 0.4s ease',
                }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <div>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', letterSpacing: '0.2em', color: '#a2cca9', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Step 01</p>
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', fontStyle: 'italic', color: '#f4f1ea', marginBottom: '2rem', fontWeight: 400 }}>
            Where Are You Now?
          </h3>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-businessName" style={labelStyle}>Business Name *</label>
            <input id="diagnostic-businessName" style={focusedInput('businessName')} value={formData.businessName}
              onChange={e => update('businessName', e.target.value)}
              onFocus={() => setFocusedField('businessName')} onBlur={() => setFocusedField(null)}
              placeholder="Your Business Name" />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-revenueTier" style={labelStyle}>Annual Revenue Tier</label>
            <select id="diagnostic-revenueTier" style={focusedInput('revenueTier')} value={formData.revenueTier}
              onChange={e => update('revenueTier', e.target.value)}
              onFocus={() => setFocusedField('revenueTier')} onBlur={() => setFocusedField(null)}>
              <option value="" disabled>Select your range</option>
              {['Under $100K','$100K–$500K','$500K–$1M','$1M–$5M','$5M+'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-industry" style={labelStyle}>Industry</label>
            <input id="diagnostic-industry" style={focusedInput('industry')} value={formData.industry}
              onChange={e => update('industry', e.target.value)}
              onFocus={() => setFocusedField('industry')} onBlur={() => setFocusedField(null)}
              placeholder="e.g. Coaching, Consulting, E-Commerce..." />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-teamSize" style={labelStyle}>Team Size</label>
            <select id="diagnostic-teamSize" style={focusedInput('teamSize')} value={formData.teamSize}
              onChange={e => update('teamSize', e.target.value)}
              onFocus={() => setFocusedField('teamSize')} onBlur={() => setFocusedField(null)}>
              <option value="" disabled>Select team size</option>
              {['Solo Founder','2–5 People','6–15 People','15+ People'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <div>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', letterSpacing: '0.2em', color: '#a2cca9', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Step 02</p>
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', fontStyle: 'italic', color: '#f4f1ea', marginBottom: '2rem', fontWeight: 400 }}>
            What's Draining Your Empire?
          </h3>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-painPoint" style={labelStyle}>Primary Bottleneck *</label>
            <textarea id="diagnostic-painPoint" style={{ ...focusedInput('painPoint'), resize: 'none', minHeight: '100px' }} rows={3}
              value={formData.painPoint} onChange={e => update('painPoint', e.target.value)}
              onFocus={() => setFocusedField('painPoint')} onBlur={() => setFocusedField(null)}
              placeholder="Describe your biggest operational bottleneck. Be specific — this is a safe space." />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-currentTools" style={labelStyle}>Tools Causing Friction</label>
            <input id="diagnostic-currentTools" style={focusedInput('currentTools')} value={formData.currentTools}
              onChange={e => update('currentTools', e.target.value)}
              onFocus={() => setFocusedField('currentTools')} onBlur={() => setFocusedField(null)}
              placeholder="e.g. Notion, Zapier, Airtable, ClickUp..." />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-hoursLost" style={labelStyle}>Hours Lost to Manual Work / Week</label>
            <select id="diagnostic-hoursLost" style={focusedInput('hoursLost')} value={formData.hoursLost}
              onChange={e => update('hoursLost', e.target.value)}
              onFocus={() => setFocusedField('hoursLost')} onBlur={() => setFocusedField(null)}>
              <option value="" disabled>Select range</option>
              {['Under 5 hrs','5–15 hrs','15–30 hrs','30+ hrs'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={{ ...labelStyle, marginBottom: '1rem' }}>Which Systems Are Broken?</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              {['Client Onboarding','Lead Nurture','Content Production','Financial Reporting','Team Coordination','Data Management'].map(opt => (
                <label key={opt} style={{
                  display: 'flex', alignItems: 'center', gap: '0.7rem',
                  padding: '0.7rem 1rem',
                  border: `1px solid ${formData.brokenSystems.includes(opt) ? 'rgba(162, 204, 169,0.5)' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: '2px',
                  cursor: 'pointer',
                  background: formData.brokenSystems.includes(opt) ? 'rgba(162, 204, 169,0.06)' : 'transparent',
                  transition: 'all 0.25s ease',
                  fontFamily: 'var(--font-sans)',
                  fontSize: '0.85rem',
                  color: formData.brokenSystems.includes(opt) ? '#f4f1ea' : '#a9a7af',
                }}>
                  <div style={{
                    width: '14px', height: '14px', borderRadius: '2px', flexShrink: 0,
                    border: `1px solid ${formData.brokenSystems.includes(opt) ? '#a2cca9' : 'rgba(255,255,255,0.2)'}`,
                    background: formData.brokenSystems.includes(opt) ? '#a2cca9' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.25s ease',
                  }}>
                    {formData.brokenSystems.includes(opt) && (
                      <svg width="8" height="8" viewBox="0 0 10 10"><path d="M2 5l3 3 3-4" stroke="#050507" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>
                    )}
                  </div>
                  <input type="checkbox" checked={formData.brokenSystems.includes(opt)} onChange={() => toggleBrokenSystem(opt)} style={visuallyHiddenStyle} />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 3 */}
      {step === 3 && (
        <div>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', letterSpacing: '0.2em', color: '#a2cca9', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Step 03</p>
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', fontStyle: 'italic', color: '#f4f1ea', marginBottom: '2rem', fontWeight: 400 }}>
            What Does Freedom Look Like?
          </h3>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-vision" style={labelStyle}>Your 90-Day Sovereign Vision *</label>
            <textarea id="diagnostic-vision" style={{ ...focusedInput('vision'), resize: 'none', minHeight: '110px' }} rows={3}
              value={formData.vision} onChange={e => update('vision', e.target.value)}
              onFocus={() => setFocusedField('vision')} onBlur={() => setFocusedField(null)}
              placeholder="If your operations ran perfectly for 90 days, what would change? Paint the picture." />
          </div>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-investmentReadiness" style={labelStyle}>Investment Readiness</label>
            <select id="diagnostic-investmentReadiness" style={focusedInput('investmentReadiness')} value={formData.investmentReadiness}
              onChange={e => update('investmentReadiness', e.target.value)}
              onFocus={() => setFocusedField('investmentReadiness')} onBlur={() => setFocusedField(null)}>
              <option value="" disabled>Select your timeline</option>
              {['Researching options','Ready within 30 days','Ready immediately','Need flexible terms'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-howFound" style={labelStyle}>How Did You Find LRC?</label>
            <select id="diagnostic-howFound" style={focusedInput('howFound')} value={formData.howFound}
              onChange={e => update('howFound', e.target.value)}
              onFocus={() => setFocusedField('howFound')} onBlur={() => setFocusedField(null)}>
              <option value="" disabled>Select source</option>
              {['Social Media','Referral','Search Engine','Podcast/Interview','Previous Client'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Step 4 */}
      {step === 4 && (
        <div>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', letterSpacing: '0.2em', color: '#a2cca9', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Step 04</p>
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', fontStyle: 'italic', color: '#f4f1ea', marginBottom: '2rem', fontWeight: 400 }}>
            Initiate Your Protocol
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' }}>
            <div style={fieldStyle}>
              <label htmlFor="diagnostic-fullName" style={labelStyle}>Full Name *</label>
              <input id="diagnostic-fullName" style={focusedInput('fullName')} value={formData.fullName}
                onChange={e => update('fullName', e.target.value)}
                onFocus={() => setFocusedField('fullName')} onBlur={() => setFocusedField(null)}
                placeholder="Your Full Name" />
            </div>
            <div style={fieldStyle}>
              <label htmlFor="diagnostic-email" style={labelStyle}>Email Address *</label>
              <input id="diagnostic-email" type="email" style={focusedInput('email')} value={formData.email}
                onChange={e => update('email', e.target.value)}
                onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
                placeholder="your@email.com" />
            </div>
          </div>
          <div style={fieldStyle}>
            <label htmlFor="diagnostic-phone" style={labelStyle}>Phone <span style={{ opacity: 0.5, fontWeight: 300 }}>(optional)</span></label>
            <input id="diagnostic-phone" type="tel" style={focusedInput('phone')} value={formData.phone}
              onChange={e => update('phone', e.target.value)}
              onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)}
              placeholder="+1 (___) ___-____" />
          </div>
          <div style={fieldStyle}>
            <label style={{ ...labelStyle, marginBottom: '1rem' }}>Preferred Consultation Format</label>
            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
              {['Video Call','Voice Call','Async (email/Loom)'].map(opt => (
                <label key={opt} style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.65rem 1.2rem',
                  border: `1px solid ${formData.consultFormat === opt ? 'rgba(162, 204, 169,0.5)' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: '2px',
                  cursor: 'pointer',
                  background: formData.consultFormat === opt ? 'rgba(162, 204, 169,0.06)' : 'transparent',
                  transition: 'all 0.25s ease',
                  fontFamily: 'var(--font-sans)',
                  fontSize: '0.85rem',
                  color: formData.consultFormat === opt ? '#a2cca9' : '#a9a7af',
                }}>
                  <div style={{
                    width: '12px', height: '12px', borderRadius: '50%', flexShrink: 0,
                    border: `1px solid ${formData.consultFormat === opt ? '#a2cca9' : 'rgba(255,255,255,0.2)'}`,
                    background: formData.consultFormat === opt ? '#a2cca9' : 'transparent',
                    transition: 'all 0.25s ease',
                  }} />
                  <input type="radio" name="consultFormat" value={opt} checked={formData.consultFormat === opt} onChange={() => update('consultFormat', opt)} style={visuallyHiddenStyle} />
                  {opt}
                </label>
              ))}
            </div>
          </div>
          <div style={{ ...fieldStyle, marginTop: '0.5rem' }}>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.8rem',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)', fontSize: '0.85rem',
              color: formData.consent ? '#f4f1ea' : '#a9a7af',
              lineHeight: 1.5,
              padding: '1rem',
              border: `1px solid ${formData.consent ? 'rgba(162, 204, 169,0.3)' : 'rgba(255,255,255,0.04)'}`,
              borderRadius: '2px',
              background: formData.consent ? 'rgba(162, 204, 169,0.04)' : 'transparent',
              transition: 'all 0.3s ease',
            }}>
              <div style={{
                width: '16px', height: '16px', borderRadius: '2px', flexShrink: 0, marginTop: '1px',
                border: `1px solid ${formData.consent ? '#a2cca9' : 'rgba(255,255,255,0.2)'}`,
                background: formData.consent ? '#a2cca9' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.25s ease',
              }}>
                {formData.consent && <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5l2.5 2.5 4-4.5" stroke="#050507" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>}
              </div>
              <input type="checkbox" checked={formData.consent} onChange={e => update('consent', e.target.checked)} style={visuallyHiddenStyle} />
              I understand this is a strategic consultation, not a sales call. I am ready to discuss my business at a high level.
            </label>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        {step > 1 ? (
          <button onClick={() => setStep(s => s - 1)} style={{
            background: 'transparent', border: 'none', color: '#a9a7af',
            fontFamily: 'var(--font-sans)', fontSize: '0.85rem', letterSpacing: '0.1em',
            textTransform: 'uppercase', cursor: 'pointer', padding: '0.5rem 0',
            transition: 'color 0.3s ease',
          }}
          onMouseOver={e => (e.currentTarget.style.color = '#f4f1ea')}
          onMouseOut={e => (e.currentTarget.style.color = '#a9a7af')}>
            ← Back
          </button>
        ) : <div />}

        {step < 4 ? (
          <button className="luxury-btn" disabled={!canAdvance()} onClick={() => setStep(s => s + 1)}
            style={{ opacity: canAdvance() ? 1 : 0.35, cursor: canAdvance() ? 'pointer' : 'not-allowed' }}>
            Continue →
          </button>
        ) : (
          <button
            className="luxury-btn"
            disabled={submitting}
            onClick={async () => {
              if (!formData.fullName.trim()) {
                setApiError('Please enter your Full Name.');
                return;
              }
              if (!formData.email.trim() || !formData.email.includes('@')) {
                setApiError('Please enter a valid Email address.');
                return;
              }
              if (!formData.consent) {
                setApiError('Please check the strategic consultation agreement box.');
                return;
              }

              setSubmitting(true);
              setApiError(null);
              try {
                await submitLead({
                  businessName: formData.businessName,
                  revenueTier: formData.revenueTier,
                  industry: formData.industry,
                  teamSize: formData.teamSize,
                  painPoint: formData.painPoint,
                  currentTools: formData.currentTools,
                  hoursLost: formData.hoursLost,
                  brokenSystems: formData.brokenSystems,
                  vision: formData.vision,
                  investmentReadiness: formData.investmentReadiness,
                  howFound: formData.howFound,
                  fullName: formData.fullName,
                  email: formData.email,
                  phone: formData.phone,
                  consultFormat: formData.consultFormat,
                });
                setSubmitted(true);
                trackFormSubmit('flow_state_diagnostic', 'success');
              } catch {
                // Still show success — data may have been received even if response parsing fails
                // (Google Apps Script sometimes closes connection before JSON is fully parsed)
                setSubmitted(true);
                trackFormSubmit('flow_state_diagnostic', 'success', { note: 'catch_recovered' });
              } finally {
                setSubmitting(false);
              }
            }}
            style={{ opacity: submitting ? 0.5 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
          >
            {submitting ? 'Submitting...' : 'Submit FlowState Diagnostic'}
          </button>
        )}
      </div>

      {apiError && (
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'rgba(255,100,100,0.7)', letterSpacing: '0.1em', marginTop: '1rem', textAlign: 'right' }}>
          {apiError}
        </p>
      )}

      <style>{`
        @keyframes dotPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
        select option { background: #0d0d11; color: #f4f1ea; }
      `}</style>
    </div>
  );
};

export default FlowStateDiagnostic;
